import React, { useState, useEffect } from 'react';
import './BaseTool.css';

const RatingComponent = ({ toolId }) => {
    const [hover, setHover] = useState(0);
    const [hasVoted, setHasVoted] = useState(false);
    const [averageRating, setAverageRating] = useState(0);
    const [totalVotes, setTotalVotes] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [showThankYou, setShowThankYou] = useState(false);

    // Cookie name to track user votes
    const COOKIE_NAME = 'bookwitch_user_votes';

    // Check if the user has already voted using a cookie
    const checkUserVoted = () => {
        const cookies = document.cookie.split(';').map(c => c.trim());
        for (const cookie of cookies) {
            if (cookie.startsWith(`${COOKIE_NAME}_${toolId}=`)) {
                return true;
            }
        }
        return false;
    };

    // Set a cookie to mark that the user has voted
    const setUserVotedCookie = () => {
        // Set cookie to expire in 30 days
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 30);
        document.cookie = `${COOKIE_NAME}_${toolId}=true; expires=${expiryDate.toUTCString()}; path=/`;
    };

    useEffect(() => {
        // Check if user has already voted
        const voted = checkUserVoted();
        setHasVoted(voted);

        // Load ratings ONLY from static JSON file
        const fetchRating = async () => {
            setIsLoading(true);
            try {
                const staticResponse = await fetch('/ratings.json');
                if (staticResponse.ok) {
                    const staticData = await staticResponse.json();
                    const toolData = staticData.tools[toolId];
                    
                    if (toolData) {
                        console.log(`Loading static ratings for ${toolId}`);
                        const average = toolData.average || 0;
                        const count = toolData.count || 0;
                        
                        setAverageRating(average > 0 ? average : 0);
                        setTotalVotes(count > 0 ? count : 0);
                    } else {
                        // Tool not found in JSON - set to 0 (won't display ratings)
                        console.log(`No ratings data found for ${toolId} in JSON`);
                        setAverageRating(0);
                        setTotalVotes(0);
                    }
                } else {
                    // JSON file not available - set to 0 (won't display ratings)
                    console.log('ratings.json not available');
                    setAverageRating(0);
                    setTotalVotes(0);
                }
            } catch (err) {
                console.error('Error loading ratings.json:', err);
                // No error display - just don't show ratings
                setAverageRating(0);
                setTotalVotes(0);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRating();
    }, [toolId]);

    const handleRating = async (value) => {
        // Note: Rating submission is now handled separately via backend
        // This component only displays static ratings from ratings.json
        // Users can still click but the rating won't be processed here
        
        setHasVoted(true);
        setShowThankYou(true);
        setUserVotedCookie();
        
        // Hide thank you message after 3 seconds
        setTimeout(() => {
            setShowThankYou(false);
        }, 3000);
        
        console.log(`User rated ${toolId} with ${value} stars (static mode - not submitted)`);
    };

    // Render stars based on a rating value
    const renderStars = (rating, interactive = false) => {
        return [...Array(5)].map((_, index) => {
            const ratingValue = index + 1;
            
            // For interactive stars (when user is voting)
            if (interactive) {
                return (
                    <span
                        key={index}
                        style={{
                            cursor: 'pointer',
                            fontSize: '30px',
                            color: ratingValue <= hover ? '#ffb400' : '#c4c4c4',
                            marginRight: '5px'
                        }}
                        onMouseEnter={() => setHover(ratingValue)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => handleRating(ratingValue)}
                    >
                        ★
                    </span>
                );
            }
            
            // For display-only stars (average rating display)
            // This handles partial stars by using a gradient between filled and empty
            const percent = Math.min(Math.max(rating - (ratingValue - 1), 0), 1) * 100;
            
            return (
                <span
                    key={index}
                    style={{
                        fontSize: '24px',
                        marginRight: '3px',
                        position: 'relative',
                        display: 'inline-block'
                    }}
                >
                    {/* Background star (gray) */}
                    <span style={{ color: '#c4c4c4', position: 'absolute' }}>★</span>
                    
                    {/* Foreground star (gold) with width based on rating */}
                    <span
                        style={{
                            color: '#ffb400',
                            position: 'absolute',
                            overflow: 'hidden',
                            width: `${percent}%`
                        }}
                    >
                        ★
                    </span>
                    
                    {/* Transparent star for proper spacing */}
                    <span style={{ color: 'transparent' }}>★</span>
                </span>
            );
        });
    };

    if (isLoading) {
        return (
            <div className="rating-component" style={{ marginTop: '30px', marginBottom: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                <p>Loading ratings...</p>
            </div>
        );
    }

    // Don't show rating component at all if no valid ratings data
    if (totalVotes === 0 && averageRating === 0) {
        return null; // Hide the component completely
    }

    return (
        <div className="rating-component" style={{ 
            marginTop: '30px', 
            marginBottom: '20px', 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            textAlign: 'center'
        }}>
            {showThankYou && (
                <div style={{ marginBottom: '15px' }}>
                    <p style={{ color: 'green', fontWeight: 'bold' }}>Thank you for your feedback!</p>
                </div>
            )}
            
            {!hasVoted ? (
                <div>
                    <h3 style={{ marginBottom: '15px' }}>How would you rate this tool?</h3>
                    <div className="star-rating" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '15px' }}>
                        {renderStars(0, true)}
                    </div>
                </div>
            ) : (
                <div className="rating-display">
                    <div style={{ marginBottom: '5px' }}>
                        {renderStars(averageRating)}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                        {totalVotes} {totalVotes === 1 ? 'review' : 'reviews'}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RatingComponent; 